// PaginaView.js — Render de una página personalizable (/p/:slug) — Fase C
// Pide la página al backend (mis-paginas.php), que ya filtra por país/rol/idioma/login.
// Si no hay coincidencia → "Página no disponible".
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageLayout } from '../contexts/PageLayoutContext';
import { misPaginasService } from '../services/api';
import DynamicLanding from '../components/landing/DynamicLanding';
import AuthModal from '../components/AuthModal';

const Centered = styled.div`
  min-height: 60vh; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 1rem; text-align: center; padding: 2rem; color: #475569;
`;

// `slug` como prop: lo usan las rutas fijas que pintan una página del proyecto
// (p. ej. /login cuando el acceso vive en su propia página). Sin prop, el slug
// sale de la URL como siempre.
const PaginaView = ({ slug: slugFijo }) => {
  const { slug: slugUrl } = useParams();
  const slug = slugFijo || slugUrl;
  const navigate = useNavigate();
  const { proyecto, loading: proyectoLoading } = useProject();
  const { idioma, t } = useIdioma();
  const { user } = useAuth();
  const { setPageLayout } = usePageLayout();

  const [estado, setEstado] = useState('loading'); // loading | ok | no-disponible
  const [pagina, setPagina] = useState(null);

  // Acceso y registro desde una sección de la página (p. ej. el botón «Acceder»
  // de una tarjeta de curso): igual que en la portada, sale el modal salvo que
  // el proyecto tenga el acceso o el registro en su propia página.
  const authCfg = proyecto?.config_json?.auth_config || {};
  const [authTab, setAuthTab] = useState('login');
  const [authOpen, setAuthOpen] = useState(false);
  const [authDestino, setAuthDestino] = useState(null);
  const openLogin = (opts = {}) => {
    if (authCfg.login_pagina) { navigate('/login'); return; }
    setAuthDestino(opts?.destino || null);
    setAuthTab('login'); setAuthOpen(true);
  };
  const openRegister = () => {
    if (authCfg.registro_pagina) { navigate('/register'); return; }
    setAuthTab('register'); setAuthOpen(true);
  };

  // Secciones que finalmente se pintan: las globales del proyecto (header/footer
  // típicamente) + las locales de la página. Si la página trae una sección del
  // mismo tipo que una global, gana la local y la global se descarta.
  // Cada global puede además restringirse por `visibilidad` (incluir/excluir
  // páginas), que se filtra por el slug actual.
  const seccionesFinales = useMemo(() => {
    if (!pagina) return [];
    const aplicaVisibilidad = (vis, ctx) => {
      if (!vis || !vis.modo || vis.modo === 'todas') return true;
      const lista = vis.paginas || [];
      if (vis.modo === 'incluir') return lista.includes(ctx);
      if (vis.modo === 'excluir') return !lista.includes(ctx);
      return true;
    };
    // `sin_header` / `sin_footer`: página a pantalla completa, sin la barra ni el pie del sitio.
    const sinHeader = pagina.config_json?.sin_header === true;
    const sinFooter = pagina.config_json?.sin_footer === true;
    const globales = (proyecto?.config_json?.secciones_globales || [])
      .filter(s => s.visible !== false)
      .filter(s => aplicaVisibilidad(s.visibilidad, pagina.slug))
      .filter(s => !(sinHeader && s.tipo === 'header') && !(sinFooter && s.tipo === 'footer'));
    const localTipos = new Set((pagina.secciones || []).map(s => s.tipo));
    const aplicables = globales.filter(g => !localTipos.has(g.tipo));
    return [
      ...aplicables.filter(s => s.tipo === 'header'),
      ...aplicables.filter(s => s.tipo !== 'header' && s.tipo !== 'footer'),
      ...(pagina.secciones || []),
      ...aplicables.filter(s => s.tipo === 'footer'),
    ];
  }, [pagina, proyecto]);

  // Avisar al layout global para que no duplique header/footer: cuenta tanto el
  // header/footer propio de la página como el global que esta vista ya pinta.
  useEffect(() => {
    // Con `sin_header`/`sin_footer` se avisa igual que los hay, para que el layout no ponga los suyos.
    const hasHeader = pagina?.config_json?.sin_header === true || seccionesFinales.some(s => s.tipo === 'header' && s.visible !== false);
    const hasFooter = pagina?.config_json?.sin_footer === true || seccionesFinales.some(s => s.tipo === 'footer' && s.visible !== false);
    setPageLayout({ pageHasHeader: hasHeader, pageHasFooter: hasFooter });
    return () => setPageLayout({ pageHasHeader: false, pageHasFooter: false });
  }, [seccionesFinales, pagina, setPageLayout]);

  useEffect(() => {
    if (proyectoLoading) return;
    if (!proyecto?.id) { setEstado('no-disponible'); return; }
    let cancel = false;
    setEstado('loading');
    misPaginasService.getBySlug(proyecto.id, slug, idioma)
      .then(res => {
        if (cancel) return;
        const p = (res.data?.paginas || [])[0];
        if (p) { setPagina(p); setEstado('ok'); }
        else { setEstado('no-disponible'); }
      })
      .catch(() => { if (!cancel) setEstado('no-disponible'); });
    return () => { cancel = true; };
  }, [proyecto?.id, proyectoLoading, slug, idioma, user]);

  // Si la página no es visible para este usuario (no logueado, audiencia que no
  // coincide, slug inexistente, etc.) redirigimos a la home en vez de mostrar
  // una pantalla bloqueada que se ve como "blanco". El usuario verá la landing
  // del proyecto, que es contenido público.
  //
  // Lo mismo con las páginas marcadas «solo para visitantes» (el acceso y el
  // registro): quien ya tiene sesión no tiene nada que hacer ahí.
  const soloInvitados = pagina?.config_json?.solo_invitados === true;
  useEffect(() => {
    if (estado === 'no-disponible') {
      navigate('/', { replace: true });
    } else if (estado === 'ok' && soloInvitados && user) {
      navigate(pagina?.config_json?.destino_con_sesion || '/', { replace: true });
    }
  }, [estado, soloInvitados, user, pagina, navigate]);

  if (estado === 'ok' && soloInvitados && user) return <Centered>{t('mentor_cargando')}</Centered>;

  if (estado === 'loading' || proyectoLoading || estado === 'no-disponible') {
    return <Centered>{t('mentor_cargando')}</Centered>;
  }

  return (
    <>
      {authOpen && <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} initialTab={authTab} urlDestino={authDestino} />}
      <DynamicLanding
        secciones={seccionesFinales}
        proyecto={proyecto}
        configOverride={pagina.config_json || null}
        openLogin={openLogin}
        openRegister={openRegister}
      />
    </>
  );
};

export default PaginaView;
